import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuthToken } from '../auth/authService';
import { isSessionValid } from '../auth/sessionManager';

const NOTIFICATIONS_URL = process.env.REACT_APP_NOTIFICATIONS_URL || 'http://localhost:8082';

function matchesRole(targetRole, userRole) {
    if (!targetRole || targetRole === 'ALL') return true;
    if (targetRole === 'NONE') return false;
    if (targetRole === 'MANAGEMENT') return ['ADMIN', 'CASHIER'].includes(userRole);
    if (targetRole === 'CUSTOMER') return ['PARENT', 'STUDENT', 'EMPLOYEE'].includes(userRole);
    return targetRole === userRole;
}

function shouldShow(notification, userRole, userId) {
    const targetedDirectly = Array.isArray(notification.targetUserIds) &&
        notification.targetUserIds.includes(userId);
    return targetedDirectly || matchesRole(notification.targetRole, userRole);
}

function showToast(notification) {
    const msg = `${notification.title}: ${notification.message}`;
    switch (notification.type) {
        case 'SUCCESS': toast.success(msg); break;
        case 'WARNING': toast.warning(msg); break;
        case 'ERROR':   toast.error(msg);   break;
        default:        toast.info(msg);    break;
    }
}

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const eventSourceRef = useRef(null);
    const location = useLocation();

    // Recibos abertos em popup (window.open) montam o App inteiro de novo, incluindo
    // este provider. Cada popup deixado aberto ficaria com uma EventSource (SSE) presa
    // indefinidamente, e sem HTTP/2 o navegador limita a ~6 conexões simultâneas por
    // origem — depois de 5-6 recibos abertos, esse limite era esgotado e tanto o SSE do
    // recibo seguinte quanto as chamadas de API das telas normais ficavam pendentes.
    // Recibos são views estáticas renderizadas a partir da query string, não precisam
    // de notificações em tempo real.
    const isPrintRoute = location.pathname.startsWith('/comprovante');

    const clientCode = sessionStorage.getItem('clientCode');
    const userRole   = sessionStorage.getItem('role');
    const userId     = sessionStorage.getItem('userId');
    const token      = getAuthToken();

    // Derived — always in sync with the notifications array, no separate state to drift.
    const unreadCount = useMemo(
        () => notifications.filter(n => !n.readByUsers?.includes(userId)).length,
        [notifications, userId]
    );

    const addNotification = useCallback((n) => {
        if (!shouldShow(n, userRole, userId)) return;
        setNotifications(prev => {
            if (prev.some(existing => existing.id === n.id)) return prev;
            return [n, ...prev];
        });
    }, [userRole, userId]);

    // Stable ref so the SSE effect never needs to re-run when addNotification changes.
    const addNotificationRef = useRef(addNotification);
    useEffect(() => { addNotificationRef.current = addNotification; }, [addNotification]);

    // Load history once per session (or when the user identity changes).
    useEffect(() => {
        if (!clientCode || !userId || !token || !isSessionValid() || isPrintRoute) return;
        fetch(`${NOTIFICATIONS_URL}/api/v1/notifications/history/${clientCode}?userId=${encodeURIComponent(userId)}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => {
                const visible = data.filter(n => shouldShow(n, userRole, userId));
                setNotifications(visible);
            })
            .catch(err => console.error('Failed to load notification history:', err));
    }, [clientCode, userId, userRole, token, isPrintRoute]);

    // SSE connection — depends only on identity, NOT on addNotification.
    // EventSource can't send custom headers, so the token travels as a query param here
    // (the only case in this file where the token isn't sent via Authorization header).
    useEffect(() => {
        if (!clientCode || !userId || !token || !isSessionValid() || isPrintRoute) return;

        let reconnectTimer = null;
        let active = true;

        function connect() {
            const es = new EventSource(
                `${NOTIFICATIONS_URL}/api/v1/notifications/stream/${clientCode}/${encodeURIComponent(userId)}?token=${encodeURIComponent(token)}`
            );
            eventSourceRef.current = es;

            es.addEventListener('foodz-alert', (e) => {
                try {
                    const notification = JSON.parse(e.data);
                    addNotificationRef.current(notification);
                    showToast(notification);
                } catch (err) {
                    console.error('Failed to parse SSE notification:', err);
                }
            });

            es.onerror = () => {
                es.close();
                if (active) reconnectTimer = setTimeout(connect, 5000);
            };
        }

        connect();

        return () => {
            active = false;
            clearTimeout(reconnectTimer);
            eventSourceRef.current?.close();
        };
    }, [clientCode, userId, token, isPrintRoute]);

    const markAsRead = useCallback(async (notificationId) => {
        if (!userId || !token) return;
        try {
            await fetch(
                `${NOTIFICATIONS_URL}/api/v1/notifications/${notificationId}/read?userId=${encodeURIComponent(userId)}`,
                { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
            );
            setNotifications(prev => prev.map(n =>
                n.id === notificationId
                    ? { ...n, readByUsers: [...(n.readByUsers || []), userId] }
                    : n
            ));
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
        }
    }, [userId, token]);

    const markAllAsRead = useCallback(async () => {
        const unread = notifications.filter(n => !n.readByUsers?.includes(userId));
        await Promise.all(unread.map(n => markAsRead(n.id)));
    }, [notifications, userId, markAsRead]);

    return (
        <NotificationsContext.Provider value={{ notifications, unreadCount, markAsRead, markAllAsRead }}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotificationsContext() {
    return useContext(NotificationsContext);
}
